import React, { useState, useEffect } from 'react';
import styles from './index.module.less';

export interface ISRDebugPanelProps {
  pageName: string;
  generatedAt: number;
  revalidate: number;
  env: string;
}

/**
 * 缓存状态类型
 * - fresh: 页面在有效期内，直接返回缓存
 * - stale: 页面已过期，返回旧缓存同时后台重新生成
 * - regenerated: 页面刚刚重新生成（5秒内）
 */
type CacheStatus = 'fresh' | 'stale' | 'regenerated';

const ISRDebugPanel: React.FC<ISRDebugPanelProps> = ({ pageName, generatedAt, revalidate, env }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [now, setNow] = useState(Date.now());

  // 只在客户端渲染时间，避免 hydration 不匹配
  useEffect(() => {
    setMounted(true);
    // 每秒更新一次，实时显示缓存状态
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 计算过期时间
  const expiresAt = generatedAt + revalidate * 1000;

  // 计算缓存状态
  const getCacheStatus = (): CacheStatus => {
    if (!mounted) return 'fresh';
    const currentTime = now;
    const pageAge = currentTime - generatedAt;

    // 如果页面生成时间在 5 秒内，认为是刚刚重新生成的
    if (pageAge < 5000) {
      return 'regenerated';
    }

    // 如果当前时间超过过期时间，页面已过期
    if (currentTime > expiresAt) {
      return 'stale';
    }

    return 'fresh';
  };

  const cacheStatus = getCacheStatus();

  // 获取状态显示文本和样式
  const getStatusDisplay = () => {
    switch (cacheStatus) {
      case 'fresh':
        return { text: 'Fresh', desc: 'Cache Hit', className: styles.fresh };
      case 'stale':
        return { text: 'Stale', desc: 'Needs Revalidate', className: styles.stale };
      case 'regenerated':
        return { text: 'New', desc: 'Just Regenerated', className: styles.regenerated };
    }
  };

  const statusDisplay = getStatusDisplay();

  // 计算剩余有效时间或过期时间
  const getTimeToLive = () => {
    if (!mounted) return '...';
    const currentTime = now;

    if (currentTime > expiresAt) {
      // 已过期，显示过期了多久
      const expiredSeconds = Math.floor((currentTime - expiresAt) / 1000);
      return `-${formatDuration(expiredSeconds)}`;
    } else {
      // 未过期，显示剩余时间
      const remainingSeconds = Math.floor((expiresAt - currentTime) / 1000);
      return formatDuration(remainingSeconds);
    }
  };

  const formatTime = (timestamp: number) => {
    if (!mounted) return '...';
    return new Date(timestamp).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDuration = (seconds: number) => {
    if (seconds >= 3600) {
      return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
    }
    if (seconds >= 60) {
      return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  // 计算页面年龄
  const getPageAge = () => {
    if (!mounted) return '...';
    const ageSeconds = Math.floor((now - generatedAt) / 1000);
    return formatDuration(ageSeconds);
  };

  return (
    <div className={`${styles.panel} ${collapsed ? styles.panelCollapsed : styles.panelExpanded}`}>
      <div
        className={`${styles.header} ${collapsed ? styles.headerCollapsed : ''}`}
        onClick={() => setCollapsed(!collapsed)}
      >
        {collapsed ? (
          <>
            <span className={`${styles.dot} ${statusDisplay.className}`} />
            <span className={styles.title}>ISR</span>
          </>
        ) : (
          <>
            <span className={styles.title}>ISR Debug</span>
            <span className={styles.env}>{env}</span>
            <span className={styles.toggle}>−</span>
          </>
        )}
      </div>
      {!collapsed && (
        <div className={styles.content}>
          {/* 缓存状态 - 最重要的信息放在最上面 */}
          <div className={styles.row}>
            <span className={styles.label}>Status:</span>
            <span className={`${styles.value} ${statusDisplay.className}`}>
              <span className={styles.statusBadge}>{statusDisplay.text}</span>
              <span className={styles.statusDesc}>{statusDisplay.desc}</span>
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>TTL:</span>
            <span className={`${styles.value} ${cacheStatus === 'stale' ? styles.stale : ''}`}>{getTimeToLive()}</span>
          </div>
          <div className={styles.divider} />
          <div className={styles.row}>
            <span className={styles.label}>Page:</span>
            <span className={styles.value} title={pageName}>
              {pageName.length > 25 ? pageName.slice(0, 25) + '...' : pageName}
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Generated:</span>
            <span className={styles.value}>{formatTime(generatedAt)}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Age:</span>
            <span className={styles.value}>{getPageAge()}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Revalidate:</span>
            <span className={styles.value}>{formatDuration(revalidate)}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Env:</span>
            <span className={styles.value}>{env}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ISRDebugPanel;
